import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Flex,
  SearchOption,
  Text,
  TypeaheadInput,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { useGetEntitiesWithSearchLazyQuery } from "~/graphql/mainHooks";
import * as styles from "../styles_vanilla.css";
import { UNLINKED_ENTITY_OPTION_ID } from "../utils";

export type EntityOption = {
  id: string;
  label: string;
  program: string | undefined;
  isUnlinked?: boolean;
};

type EntityTypeaheadProps = {
  arenaId?: string;
  onSelect: (option: EntityOption) => void;
  onClear: () => void;
  inputRef: React.RefObject<TypeaheadInputComponent>;
  disabled?: boolean;
  placeholder?: string;
  initialSearchValue?: string;
  errorMessage?: string;
};

export const EntityTypeahead = ({
  arenaId,
  onSelect,
  onClear,
  inputRef,
  disabled,
  placeholder,
  initialSearchValue,
  errorMessage,
}: EntityTypeaheadProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [resultsArenaId, setResultsArenaId] = useState<string | undefined>();
  const arenaIdRef = useRef(arenaId);
  arenaIdRef.current = arenaId;
  const [getEntities, { loading, error, data }] = useGetEntitiesWithSearchLazyQuery({
    variables: { arenaId: undefined, searchTerm: "", filterEntitiesWithTransactions: false },
    fetchPolicy: "no-cache",
    context: withClientContext("staff"),
  });

  useEffect(() => {
    setResultsArenaId(undefined);
    setSearchTerm("");
  }, [arenaId]);

  const nodes = useMemo(() => {
    if (resultsArenaId !== arenaId) return [];
    return (
      data?.investmentEntitiesByArena?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) ?? []
    );
  }, [data, resultsArenaId, arenaId]);

  const options: SearchOption[] = useMemo(() => {
    if (resultsArenaId !== arenaId) return [];
    if (nodes.length > 0) {
      return nodes.map((node) => ({ id: node.id, label: node.name, type: "account" }));
    }
    const trimmed = searchTerm.trim();
    if (!trimmed || loading || error) return [];
    return [
      {
        id: UNLINKED_ENTITY_OPTION_ID,
        label: trimmed,
        customLabel: (
          <Flex
            flexDirection="column"
            gap={Sizes.XS}
            className={styles.unlinkedOption}
            data-testid="unlinked-entity-option"
          >
            <Text variant="secondary-sm" className={styles.unlinkedOptionHint}>
              Entity does not exist in Juniper Square workspace
            </Text>
            <Text variant="regular-md">{`Continue with unlinked case for "${trimmed}"`}</Text>
          </Flex>
        ),
      },
    ];
  }, [nodes, searchTerm, loading, error, resultsArenaId, arenaId]);

  const waitingForClientResults = searchTerm.trim() !== "" && resultsArenaId !== arenaId;

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      const requestedArenaId = arenaId;
      void getEntities({
        variables: { searchTerm: term.trim(), arenaId, filterEntitiesWithTransactions: false },
      }).then((result) => {
        if (arenaIdRef.current !== requestedArenaId) return;
        if (result.error) return;
        setResultsArenaId(requestedArenaId);
      });
    },
    [arenaId, getEntities],
  );

  const handleSelect = useCallback(
    (selected: SearchOption) => {
      if (selected.id === UNLINKED_ENTITY_OPTION_ID) {
        onSelect({
          id: "",
          label: selected.label,
          program: undefined,
          isUnlinked: true,
        });
        return;
      }
      const program = nodes.find((node) => node.id === selected.id)?.complianceSettings
        ?.complianceProgram;
      onSelect({
        id: selected.id.toString(),
        label: selected.label,
        program: program?.toString(),
        isUnlinked: false,
      });
    },
    [nodes, onSelect],
  );

  return (
    <TypeaheadInput
      key={arenaId ?? "no-client"}
      label="Entity"
      name="entityId"
      disabled={disabled || !arenaId}
      placeholder={placeholder ?? ""}
      className={styles.typeaheadField}
      options={options}
      isLoading={loading || waitingForClientResults}
      isError={error !== undefined}
      errorMessage={errorMessage}
      onDebouncedSearch={handleSearch}
      onSelectOption={handleSelect}
      onClearValue={onClear}
      initialSearchValue={initialSearchValue}
      noResultsFoundLabel="Entity does not exist in Juniper Square workspace"
      ref={inputRef}
    />
  );
};
